import * as React from "react";
import { useWebSocket, ReadyState } from "@/lib/cjs-interop";
import type { SnapshotQaEvent } from "./types";

interface Props {
  queryId: string | null;
  onRoutingResult: (mode: string, reasoning: string) => void;
  onProseChunk: (chunk: string) => void;
  onProseComplete: (citations: string) => void;
  onFollowUp: (question: string) => void;
  onError: (message: string) => void;
  onEnd: () => void;
  onListRetrieving: () => void;
  onListDocuments: (count: number) => void;
  onListResults: (results: string) => void;
  onFieldsDetecting: () => void;
  onFieldsDetected: (fields: string[], count: number, reused: boolean) => void;
  onSource: (source: string) => void;
  onRewriting: () => void;
  onContextSize: (tokens: number, documents: number, mapReduce: boolean) => void;
  onMapProgress: (done: number, total: number) => void;
  onScope: (strategy: string, hyde: boolean, reasoning: string) => void;
  onRetrieving: (strategy: string, candidates: number, selectedDocuments: number) => void;
}

const toInt = (value: string | undefined): number => {
  const parsed = parseInt(value ?? "0", 10);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const safeParse = (json: string | undefined, fallback: Record<string, unknown> = {}): Record<string, unknown> => {
  try {
    return JSON.parse(json ?? "{}");
  } catch (e) {
    console.error("Failed to parse WebSocket event context:", json?.slice(0, 200), e);
    return fallback;
  }
};

export const SnapshotQaWebsocket: React.FC<Props> = ({
  queryId,
  onRoutingResult,
  onProseChunk,
  onProseComplete,
  onFollowUp,
  onError,
  onEnd,
  onListRetrieving,
  onListDocuments,
  onListResults,
  onFieldsDetecting,
  onFieldsDetected,
  onSource,
  onRewriting,
  onContextSize,
  onMapProgress,
  onScope,
  onRetrieving,
}) => {
  const { lastJsonMessage, readyState, sendJsonMessage } = useWebSocket<SnapshotQaEvent>(
    "/pf-mainweb/business-reports/ws",
    {
      shouldReconnect: event => event?.code !== 1000,
      reconnectInterval: 3000,
      skipAssert: true,
    },
  );

  React.useEffect(() => {
    if (readyState === ReadyState.OPEN && queryId) {
      sendJsonMessage({ query_id: queryId });
    }
  }, [readyState, queryId, sendJsonMessage]);

  React.useEffect(() => {
    if (!lastJsonMessage) return;

    switch (lastJsonMessage.event) {
      case "routing": {
        const context = safeParse(lastJsonMessage.context);
        onRoutingResult((context.mode as string) ?? "", (context.reasoning as string) ?? "");
        break;
      }
      case "fields_detecting": {
        onFieldsDetecting();
        break;
      }
      case "fields_detected": {
        let fields: string[] = [];
        try {
          fields = JSON.parse(lastJsonMessage.fields ?? "[]");
        } catch (e) {
          console.error("Failed to parse fields_detected:", e);
          fields = [];
        }
        const count = parseInt(lastJsonMessage.count ?? "0", 10);
        const reused = lastJsonMessage.reused === "true";
        onFieldsDetected(fields, isNaN(count) ? fields.length : count, reused);
        break;
      }
      case "source": {
        onSource(lastJsonMessage.source);
        break;
      }
      case "rewriting": {
        onRewriting();
        break;
      }
      case "context_size": {
        onContextSize(
          toInt(lastJsonMessage.tokens),
          toInt(lastJsonMessage.documents),
          lastJsonMessage.map_reduce === "true",
        );
        break;
      }
      case "map_progress": {
        onMapProgress(toInt(lastJsonMessage.done), toInt(lastJsonMessage.total));
        break;
      }
      case "scope": {
        onScope(lastJsonMessage.strategy ?? "", lastJsonMessage.hyde === "true", lastJsonMessage.reasoning ?? "");
        break;
      }
      case "retrieving": {
        onRetrieving(
          lastJsonMessage.strategy ?? "",
          toInt(lastJsonMessage.candidates),
          toInt(lastJsonMessage.selected_documents),
        );
        break;
      }
      case "prose_chunk": {
        onProseChunk(lastJsonMessage.t);
        break;
      }
      case "prose_complete": {
        onProseComplete(lastJsonMessage.citations);
        break;
      }
      case "follow_up": {
        onFollowUp(lastJsonMessage.t);
        break;
      }
      case "prose_error":
      case "error": {
        onError(lastJsonMessage.message);
        break;
      }
      case "end": {
        onEnd();
        break;
      }
      case "list_retrieving": {
        onListRetrieving();
        break;
      }
      case "list_documents": {
        const parsed = parseInt(lastJsonMessage.count, 10);
        onListDocuments(Number.isNaN(parsed) ? 0 : parsed);
        break;
      }
      case "list_results": {
        onListResults(lastJsonMessage.context);
        break;
      }
      default: {
        console.warn("Unknown snapshot QA event:", (lastJsonMessage as Record<string, unknown>).event);
        break;
      }
    }
  }, [
    lastJsonMessage,
    onRoutingResult,
    onProseChunk,
    onProseComplete,
    onFollowUp,
    onError,
    onEnd,
    onListRetrieving,
    onListDocuments,
    onListResults,
    onFieldsDetecting,
    onFieldsDetected,
    onSource,
    onRewriting,
    onContextSize,
    onMapProgress,
    onScope,
    onRetrieving,
  ]);

  return null;
};
